
import {
  defineComponent, computed, reactive, onBeforeMount,
} from 'vue';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import Icone from '@/core/components/Icone.vue';
import { ECustomRenderRow, IColumn } from '@/core/models/AntDesign/IColumn';
import { IListaPaginadaMetaData } from '@/core/models/Consulta/IListaPaginada';
import { useGradeBase } from '@/core/composables/GradeBase';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import { IPagination } from '@/core/models/AntDesign/IPagination';
import MensagemSemDados from '@/core/components/Tela/MensagemSemDados.vue';
import UtilitarioDispositivo from '@/core/utilitarios/UtilitarioDispositivo';
import { IDTONotaFiscalServico } from '@/models/DTO/Fiscal/NotasFiscaisServico/IDTONotaFiscalServico';

export default defineComponent({
  name: 'NotaFiscalServicoListagem',
  components: {
    Icone,
    MensagemSemDados,
  },
  props: {
    visualizacaoKanban: {
      type: Boolean,
      default: true,
    },
    notasServicos: {
      type: Array as () => IDTONotaFiscalServico[],
      required: true,
    },
    selecionados: {
      type: Array as () => number[],
      required: true,
    },
    paginacao: {
      type: Object as () => IPagination,
      required: true,
    },
  },
  emits: ['update:notasServicos', 'update:visualizacaoKanban', 'update:paginacao', 'update:selecionados', 'selecaoAlterada', 'buscarNotasServicos', 'visualizar', 'imprimir', 'novoLancamento'],
  setup(props, { emit }) {
    const { telaBase } = useTelaBase();
    const { gradeBase, preencheOrdenacaoSelecionada, ordenacaoAtiva } = useGradeBase();

    const state = reactive({
      colunas: [] as IColumn[],
      paginacaoApi: { totalRegistros: 0, paginaAtual: 1 } as IListaPaginadaMetaData,
      timerRegistros: 0,
      totalRegistrosComMascara: '100',
      telaMobile: false,
    });

    const computednotasServicos = computed({
      get: () => props.notasServicos,
      set: (valor: IDTONotaFiscalServico[]) => {
        emit('update:notasServicos', valor);
      },
    });

    const computedVisualizacaoKanban = computed({
      get: () => props.visualizacaoKanban,
      set: (valor: boolean) => {
        emit('update:visualizacaoKanban', valor);
      },
    });

    const computedSelecionados = computed({
      get: () => props.selecionados,
      set: (valor: number[]) => {
        emit('update:selecionados', valor);
      },
    });

    const computedPaginacao = computed({
      get: () => props.paginacao,
      set: (valor: IPagination) => {
        emit('update:paginacao', valor);
      },
    });

    function obterClasseStatus(status: string) {
      switch (status) {
        case 'Pendente':
          return 'em-digitacao';
        case 'Em Processamento':
          return 'aguardando-emissao';
        case 'Autorizada':
          return 'concluido';
        default:
          return 'cancelado';
      }
    }

    function preencherColunas() {
      if (state.telaMobile) {
        state.colunas = [
          {
            title: 'Código', dataIndex: 'codigo', key: 'Codigo', position: 1, visible: false,
          },
          {
            title: 'NF Serviço', dataIndex: 'notaFiscalServico', key: 'NotaFiscalServico', position: 2, visible: true, ellipsis: true,
          },
        ];
      } else {
        state.colunas = [
          {
            title: 'Código', dataIndex: 'codigo', key: 'Codigo', position: 1, visible: false, ordering: true, fixed: 'left', customRenderRow: ECustomRenderRow.LinkAcao,
          },
          {
            title: 'Nº RPS', dataIndex: 'numeroRps', key: 'NumeroRps', position: 2, visible: true, ordering: true, fixed: 'left', customRenderRow: ECustomRenderRow.LinkAcao, width: 110,
          },
          {
            title: 'Dt de Emissão', dataIndex: 'dataEmissaoRps', key: 'DataEmissaoRps', position: 3, visible: true, ordering: true, customRenderRow: ECustomRenderRow.MascararData, width: 120, align: 'center',
          },
          {
            title: 'Status', dataIndex: 'descricaoStatus', key: 'Status', position: 4, visible: true, ordering: true, width: 250, align: 'center',
          },
          {
            title: 'Nº NFS-e', dataIndex: 'numeroNfse', key: 'NumeroNfse', position: 5, visible: true, ordering: true, width: 100,
          },
          {
            title: 'Chave', dataIndex: 'chave', key: 'Chave', position: 6, visible: true, ordering: true, width: 75, align: 'center',
          },
          {
            title: 'Tomador', dataIndex: 'nomeRazaoSocialTomador', key: 'NomeRazaoSocialTomador', position: 7, visible: true, ordering: true,
          },
          {
            title: 'Valor Total', dataIndex: 'totalNotaFiscal', key: 'TotalNotaFiscal', position: 8, visible: true, ordering: true, customRenderRow: ECustomRenderRow.MascararFracionado, align: 'right', width: 100,
          },
          {
            title: 'Ações', key: 'acoes', position: 8, visible: true, fixed: 'right', width: 100, align: 'center', customRenderRow: ECustomRenderRow.IconeAcoes,
          },
        ];
      }
    }

    async function ordenarDados(key: string, sort: string) {
      await preencheOrdenacaoSelecionada(key, sort);
      emit('buscarNotasServicos', gradeBase.ordenacaoSelecionada);
    }

    async function alterarQuantidadeRegistroTotal(valor: any) {
      clearTimeout(state.timerRegistros);
      let totalRegistros = 100;
      state.timerRegistros = setTimeout(async () => {
        if (UtilitarioGeral.valorValido(valor)) {
          const limiteRegistros = UtilitarioMascara.removerMascaraNumero(valor);
          if (limiteRegistros > 0) {
            totalRegistros = limiteRegistros;
          }
        }
        computedPaginacao.value.total = totalRegistros;
        state.totalRegistrosComMascara = UtilitarioMascara.mascararNumeroInteiro(totalRegistros);
        emit('buscarNotasServicos');
      }, 600);
    }

    async function mudarPagina(current: number, pageSize: number) {
      computedPaginacao.value.current = current;
      computedPaginacao.value.pageSize = pageSize;
      emit('buscarNotasServicos');
    }

    async function mostrarNovaPagina(current: number, pageSize: number) {
      computedPaginacao.value.current = 1;
      computedPaginacao.value.pageSize = pageSize;
      emit('buscarNotasServicos');
    }

    const mudarSelecao = (selectedRowKeys: any) => {
      computedSelecionados.value = selectedRowKeys;
      emit('selecaoAlterada');
    };

    function visualizar(index: number) {
      emit('visualizar', props.notasServicos[index].codigo);
    }

    function imprimir(index: number) {
      emit('imprimir', props.notasServicos[index].codigo);
    }

    function novoLancamento() {
      emit('novoLancamento');
    }

    onBeforeMount(async () => {
      state.telaMobile = UtilitarioDispositivo.larguraTelaMobile();
      preencherColunas();
      computedPaginacao.value.total = 100;
      computedPaginacao.value.showSizeChanger = true;
      computedPaginacao.value.pageSize = 100;
      computedPaginacao.value.pageSizeOptions = ['10', '50', '100', '500', '1000'];
    });

    return {
      state,
      props,
      window,
      telaBase,
      gradeBase,
      computednotasServicos,
      computedVisualizacaoKanban,
      computedSelecionados,
      computedPaginacao,
      UtilitarioMascara,
      UtilitarioData,
      mudarPagina,
      ordenarDados,
      mostrarNovaPagina,
      alterarQuantidadeRegistroTotal,
      obterClasseStatus,
      mudarSelecao,
      ordenacaoAtiva,
      ECustomRenderRow,
      visualizar,
      imprimir,
      novoLancamento,
    };
  },
});

